import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, IconButton, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import playstoreBadgeWhite from "../../assets/playstore-badge-white.webp";
import appStoreBadge from "../../assets/app-store-badge.webp";

export const MOBILE_BANNER_HEIGHT = 50;

interface MobileAppBannerProps {
  onClose?: () => void;
}

const isIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

const MobileAppBanner: React.FC<MobileAppBannerProps> = ({ onClose }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const bannerClosed = sessionStorage.getItem("mobileAppBannerClosed");
    if (!bannerClosed && isSmDown) {
      setIsVisible(true);
    }
  }, [isSmDown]);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem("mobileAppBannerClosed", "true");
    onClose?.();
  };

  const ios = isIOS();
  if (!isSmDown || !isVisible) return null;

  return (
    <Box
      sx={{
        height: MOBILE_BANNER_HEIGHT,
        backgroundColor: "secondary.light",
        color: "primary.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
        {t("mobileAppBanner.title")}<br />{t("mobileAppBanner.subtitle")}
      </Typography>

      {ios ? (
        <a
          href="https://apps.apple.com/in/app/only-one-tap-home-services/id6789015962"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", flexShrink: 0, marginLeft: 8 }}
        >
          <img
            src={appStoreBadge}
            alt="App Store"
            width={110}
            height={32}
            style={{ objectFit: "contain" }}
          />
        </a>
      ) : (
        <a
          href="https://play.google.com/store/apps/details?id=com.indileap.onlyonetap"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", flexShrink: 0, marginLeft: 8 }}
        >
          <img
            src={playstoreBadgeWhite}
            alt="Playstore"
            width={110}
            height={32}
            style={{ objectFit: "contain" }}
          />
        </a>
      )}

      <IconButton
        onClick={handleClose}
        size="small"
        sx={{ color: "primary", flexShrink: 0, ml: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default MobileAppBanner;
